// frontend/src/pages/OrgSettingsPage.jsx
import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import * as orgsApi from "../api/orgs";
import ConfirmDialog from "../components/ConfirmDialog";

const ROLE_BADGE = {
  owner: "bg-purple-100 text-purple-700",
  admin: "bg-blue-100 text-blue-700",
  member: "bg-gray-100 text-gray-600",
};
const ALL_ROLES = ["owner", "admin", "member"];
const INVITE_ROLES = ["member", "admin"];

/** Small coloured pill for a role. */
function RoleBadge({ role }) {
  return (
    <span
      className={`px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide ${
        ROLE_BADGE[role] || ROLE_BADGE.member
      }`}
    >
      {role}
    </span>
  );
}
RoleBadge.propTypes = { role: PropTypes.string.isRequired };

/** Two-letter initials avatar derived from an email. */
function Avatar({ email }) {
  const initials = (email.split("@")[0] || email).slice(0, 2).toUpperCase();
  return (
    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
      {initials}
    </span>
  );
}
Avatar.propTypes = { email: PropTypes.string.isRequired };

/**
 * OrgSettingsPage — manage the active organisation.
 *
 * Section 1 (General): name + slug (editable by admin+).
 * Section 2 (Members): member table; Remove (admin+), Change role (owner).
 * Section 3 (Invitations): invite by email + role; revoke pending (admin+).
 */
function OrgSettingsPage() {
  const { user, currentOrg, reloadOrgs } = useAuth();

  const role = currentOrg?.role;
  const isAdmin = role === "admin" || role === "owner";
  const isOwner = role === "owner";

  // ── General ──────────────────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [savingGeneral, setSavingGeneral] = useState(false);

  useEffect(() => {
    if (currentOrg) {
      setName(currentOrg.name || "");
      setSlug(currentOrg.slug || "");
    }
  }, [currentOrg]);

  // ── Members + invitations ────────────────────────────────────────────────
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);

  // ── Confirm dialog (remove member / revoke invite) ───────────────────────
  const [confirm, setConfirm] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const loadMembers = useCallback(async () => {
    try {
      setMembers(await orgsApi.getMembers());
    } catch {
      toast.error("Could not load members.");
    }
  }, []);

  const loadInvitations = useCallback(async () => {
    if (!isAdmin) return;
    try {
      setInvitations(await orgsApi.listInvitations());
    } catch {
      toast.error("Could not load invitations.");
    }
  }, [isAdmin]);

  useEffect(() => {
    loadMembers();
    loadInvitations();
  }, [loadMembers, loadInvitations]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const saveGeneral = async (e) => {
    e.preventDefault();
    setSavingGeneral(true);
    try {
      await orgsApi.updateOrg({ name: name.trim(), slug: slug.trim() });
      await reloadOrgs(); // refresh the switcher + currentOrg
      toast.success("Organisation updated.");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) toast.error("That slug is already taken.");
      else if (status === 422) toast.error("Check the name and slug format.");
      else if (status === 403) toast.error("You don't have permission to do that.");
      else toast.error("Could not update the organisation.");
    } finally {
      setSavingGeneral(false);
    }
  };

  const onRoleChange = async (userId, newRole) => {
    try {
      await orgsApi.changeMemberRole(userId, newRole);
      toast.success("Role updated.");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) toast.error("Cannot demote the last owner.");
      else if (status === 403) toast.error("Only owners can change roles.");
      else toast.error("Could not change the role.");
    } finally {
      await loadMembers(); // re-sync the select (revert on failure)
    }
  };

  const askRemove = (member) => {
    setConfirm({
      title: "Remove member",
      message: `Remove ${member.email} from this organisation?`,
      confirmLabel: "Remove",
      onConfirm: async () => {
        setConfirmLoading(true);
        try {
          await orgsApi.removeMember(member.user_id);
          toast.success("Member removed.");
          await loadMembers();
        } catch (err) {
          const status = err?.response?.status;
          if (status === 409) toast.error("Cannot remove the last owner.");
          else if (status === 403) toast.error("You can't remove that member.");
          else if (status === 404) toast.error("That member no longer exists.");
          else toast.error("Could not remove the member.");
        } finally {
          setConfirmLoading(false);
          setConfirm(null);
        }
      },
    });
  };

  const onInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await orgsApi.createInvitation(inviteEmail.trim(), inviteRole);
      toast.success("Invitation sent.");
      setInviteEmail("");
      await loadInvitations();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) toast.error("That email is already a member.");
      else if (status === 422) toast.error("Enter a valid email address.");
      else toast.error("Could not send the invitation.");
    } finally {
      setInviting(false);
    }
  };

  const askRevoke = (invitation) => {
    setConfirm({
      title: "Revoke invitation",
      message: `Revoke the invitation for ${invitation.invited_email}?`,
      confirmLabel: "Revoke",
      onConfirm: async () => {
        setConfirmLoading(true);
        try {
          await orgsApi.revokeInvitation(invitation.id);
          toast.success("Invitation revoked.");
          await loadInvitations();
        } catch {
          toast.error("Could not revoke the invitation.");
        } finally {
          setConfirmLoading(false);
          setConfirm(null);
        }
      },
    });
  };

  if (!currentOrg) {
    return <p className="text-sm text-gray-500">Loading organisation…</p>;
  }

  const inputBase =
    "w-full rounded-lg border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900 mb-1">
          Organisation settings
        </h1>
        <p className="text-sm text-gray-500">
          Manage your organisation, its members and invitations.
        </p>
      </div>

      {/* ── Section 1: General ──────────────────────────────────────────── */}
      <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">General</h2>
        <form onSubmit={saveGeneral} className="space-y-4">
          <div>
            <label htmlFor="org-name" className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              id="org-name"
              type="text"
              value={name}
              maxLength={200}
              disabled={!isAdmin}
              onChange={(e) => setName(e.target.value)}
              className={`${inputBase} ${
                isAdmin ? "border-gray-300" : "border-gray-200 bg-gray-50 text-gray-500"
              }`}
            />
          </div>
          <div>
            <label htmlFor="org-slug" className="block text-sm font-medium text-gray-700 mb-1">
              Slug
            </label>
            <input
              id="org-slug"
              type="text"
              value={slug}
              disabled={!isAdmin}
              onChange={(e) => setSlug(e.target.value)}
              className={`${inputBase} ${
                isAdmin ? "border-gray-300" : "border-gray-200 bg-gray-50 text-gray-500"
              }`}
            />
            <p className="mt-1 text-xs text-gray-400">
              Lowercase letters, digits and hyphens. Used in URLs.
            </p>
          </div>
          {isAdmin && (
            <button
              type="submit"
              disabled={savingGeneral}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {savingGeneral ? "Saving…" : "Save changes"}
            </button>
          )}
        </form>
      </section>

      {/* ── Section 2: Members ──────────────────────────────────────────── */}
      <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Members</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-200">
                <th className="py-2 font-medium">Member</th>
                <th className="py-2 font-medium">Role</th>
                <th className="py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const canRemove =
                  isAdmin && m.role !== "owner" && m.user_id !== user?.id;
                return (
                  <tr key={m.user_id} className="border-b border-gray-100 last:border-0">
                    <td className="py-3">
                      <div className="flex items-center gap-3">
                        <Avatar email={m.email} />
                        <div className="min-w-0">
                          <div className="truncate text-gray-800">{m.email}</div>
                          {m.display_name && (
                            <div className="truncate text-xs text-gray-400">
                              {m.display_name}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3">
                      {isOwner ? (
                        <select
                          aria-label={`Role for ${m.email}`}
                          value={m.role}
                          onChange={(e) => onRoleChange(m.user_id, e.target.value)}
                          className="rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {ALL_ROLES.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <RoleBadge role={m.role} />
                      )}
                    </td>
                    <td className="py-3 text-right">
                      {canRemove && (
                        <button
                          type="button"
                          onClick={() => askRemove(m)}
                          className="text-sm text-red-600 hover:text-red-700 font-medium"
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Section 3: Invitations (admin+) ─────────────────────────────── */}
      {isAdmin && (
        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Invitations</h2>

          <form onSubmit={onInvite} className="flex flex-col sm:flex-row gap-3 mb-5">
            <input
              type="email"
              required
              placeholder="teammate@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              aria-label="Invitation role"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="rounded-lg border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {INVITE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <button
              type="submit"
              disabled={inviting}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {inviting ? "Inviting…" : "Invite"}
            </button>
          </form>

          {invitations.length === 0 ? (
            <p className="text-sm text-gray-400">No pending invitations.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {invitations.map((inv) => (
                <li
                  key={inv.id}
                  className="flex items-center justify-between gap-3 py-3"
                >
                  <div className="min-w-0 flex items-center gap-3">
                    <span className="truncate text-sm text-gray-800">
                      {inv.invited_email}
                    </span>
                    <RoleBadge role={inv.role} />
                  </div>
                  <button
                    type="button"
                    onClick={() => askRevoke(inv)}
                    className="text-sm text-red-600 hover:text-red-700 font-medium shrink-0"
                  >
                    Revoke
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          dangerous
          loading={confirmLoading}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

export default OrgSettingsPage;
